import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Divider,
    Grid,
    Paper,
    Skeleton,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import {
    Groups as UsersIcon,
    Store as EstablishmentIcon,
    Business as ClientCompanyIcon,
    FilterList as FilterIcon,
    Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
    Chart as ChartJS,
    ArcElement,
    BarElement,
    CategoryScale,
    LinearScale,
    Tooltip,
    Legend,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';

import { Toolbar } from '../../shared/components';
import { LayoutBaseDePagina } from '../../shared/layouts';
import { DashboardService } from '../../shared/services';
import {
    IAdminDashboardSummary,
    IDashboardFilters,
} from '../../shared/protocolos';

ChartJS.register(
    ArcElement,
    BarElement,
    CategoryScale,
    LinearScale,
    Tooltip,
    Legend,
);

type TKpiCardProps = {
    title: string;
    value: number | string;
    icon: ReactNode;
    color: string;
    loading: boolean;
    helper?: string;
};

const emptySummary: IAdminDashboardSummary = {
    totalUsers: 0,
    totalEstablishments: 0,
    totalClientCompanies: 0,
};

const DashboardKpiCard = ({
    title,
    value,
    icon,
    color,
    loading,
    helper,
}: TKpiCardProps) => (
    <Card
        sx={{
            height: '100%',
            borderRadius: 3,
            background: `linear-gradient(135deg, ${color}18 0%, ${color}06 100%)`,
            border: `1px solid ${color}30`,
            transition: 'all 0.2s ease',
            '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 10px 28px ${color}25`,
            },
        }}
    >
        <CardContent sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
                <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {title}
                    </Typography>

                    {loading ? (
                        <Skeleton width={90} height={46} />
                    ) : (
                        <Typography variant="h4" sx={{ fontWeight: 800, color }}>
                            {value}
                        </Typography>
                    )}

                    {helper && (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{ mt: 1 }}
                        >
                            {helper}
                        </Typography>
                    )}
                </Box>

                <Avatar sx={{ bgcolor: color, width: 56, height: 56 }}>
                    {icon}
                </Avatar>
            </Box>
        </CardContent>
    </Card>
);

export const Dashboard = () => {
    const theme = useTheme();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [summary, setSummary] = useState<IAdminDashboardSummary>(emptySummary);

    const [filters, setFilters] = useState<IDashboardFilters>({
        startDate: '',
        endDate: '',
    });

    const loadDashboard = useCallback(async (filtersToApply?: IDashboardFilters) => {
        try {
            setLoading(true);
            setError('');

            const result = await DashboardService.getAdminSummary(filtersToApply);

            if (result instanceof Error) {
                setError(result.message);
                return;
            }

            setSummary(result);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadDashboard();
    }, [loadDashboard]);

    const handleApplyFilters = () => {
        if (filters.startDate && filters.endDate && filters.endDate < filters.startDate) {
            setError('A data final não pode ser anterior à data inicial.');
            return;
        }

        loadDashboard(filters);
    };

    const handleClearFilters = () => {
        const cleanFilters = {
            startDate: '',
            endDate: '',
        };

        setFilters(cleanFilters);
        loadDashboard(cleanFilters);
    };

    const labels = ['Usuários', 'Estabelecimentos', 'Empresas Parceiras'];

    const values = useMemo(() => ([
        summary.totalUsers,
        summary.totalEstablishments,
        summary.totalClientCompanies,
    ]), [summary]);

    const barData = {
        labels,
        datasets: [
            {
                label: 'Total',
                data: values,
                borderWidth: 1,
                borderRadius: 8,
                backgroundColor: [
                    theme.palette.primary.main,
                    theme.palette.success.main,
                    theme.palette.info.main,
                ],
            },
        ],
    };

    const doughnutData = {
        labels,
        datasets: [
            {
                data: values,
                backgroundColor: [
                    theme.palette.primary.main,
                    theme.palette.success.main,
                    theme.palette.info.main,
                ],
                borderColor: theme.palette.background.paper,
                borderWidth: 2,
            },
        ],
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom' as const,
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    precision: 0,
                },
            },
        },
    };

    const doughnutOptions = {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '60%',
        plugins: {
            legend: {
                position: 'bottom' as const,
            },
        },
    };

    const usersPerEstablishment = summary.totalEstablishments
        ? summary.totalUsers / summary.totalEstablishments
        : 0;

    const usersPerClientCompany = summary.totalClientCompanies
        ? summary.totalUsers / summary.totalClientCompanies
        : 0;

    const clientCompaniesPerEstablishment = summary.totalEstablishments
        ? summary.totalClientCompanies / summary.totalEstablishments
        : 0;

    return (
        <LayoutBaseDePagina barraDeFerramentas={<Toolbar mostrarBotaoNovo={false} />}>
            <Box sx={{ width: '100%', p: { xs: 1, md: 3 } }}>
                <Paper
                    elevation={0}
                    sx={{
                        p: 3,
                        mb: 3,
                        borderRadius: 3,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}14 0%, ${theme.palette.background.paper} 70%)`,
                        border: `1px solid ${theme.palette.divider}`,
                    }}
                >
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 800,
                            color: theme.palette.primary.main,
                        }}
                    >
                        Dashboard Administrativo
                    </Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Visão geral de usuários, estabelecimentos e empresas parceiras.
                    </Typography>

                    <Divider sx={{ my: 3 }} />

                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={4} md={3}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Data inicial"
                                type="date"
                                value={filters.startDate}
                                onChange={(event) => setFilters(prev => ({
                                    ...prev,
                                    startDate: event.target.value,
                                }))}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>

                        <Grid item xs={12} sm={4} md={3}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Data final"
                                type="date"
                                value={filters.endDate}
                                onChange={(event) => setFilters(prev => ({
                                    ...prev,
                                    endDate: event.target.value,
                                }))}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>

                        <Grid item xs={12} sm={4} md={6}>
                            <Box display="flex" gap={1} flexWrap="wrap">
                                <Button
                                    variant="contained"
                                    startIcon={<FilterIcon />}
                                    onClick={handleApplyFilters}
                                    disabled={loading}
                                >
                                    Aplicar
                                </Button>

                                <Button
                                    variant="outlined"
                                    onClick={handleClearFilters}
                                    disabled={loading}
                                >
                                    Limpar
                                </Button>

                                <Button
                                    variant="text"
                                    startIcon={<RefreshIcon />}
                                    onClick={() => loadDashboard(filters)}
                                    disabled={loading}
                                >
                                    Atualizar
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>

                {error && (
                    <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                        {error}
                    </Alert>
                )}

                <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={4}>
                        <DashboardKpiCard
                            title="Total de Usuários"
                            value={summary.totalUsers}
                            icon={<UsersIcon />}
                            color={theme.palette.primary.main}
                            loading={loading}
                            helper="Usuários cadastrados na plataforma"
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <DashboardKpiCard
                            title="Total de Estabelecimentos"
                            value={summary.totalEstablishments}
                            icon={<EstablishmentIcon />}
                            color={theme.palette.success.main}
                            loading={loading}
                            helper="Estabelecimentos ativos/cadastrados"
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <DashboardKpiCard
                            title="Empresas Parceiras"
                            value={summary.totalClientCompanies}
                            icon={<ClientCompanyIcon />}
                            color={theme.palette.info.main}
                            loading={loading}
                            helper="Empresas cliente vinculadas"
                        />
                    </Grid>
                </Grid>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={8}>
                        <Card sx={{ borderRadius: 3, height: 420 }}>
                            <CardContent sx={{ height: '100%' }}>
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    mb={2}
                                >
                                    <Box>
                                        <Typography variant="h6" fontWeight={700}>
                                            Visão comparativa
                                        </Typography>

                                        <Typography variant="body2" color="text.secondary">
                                            Comparativo dos principais indicadores administrativos
                                        </Typography>
                                    </Box>

                                    <Chip label="Resumo" color="primary" variant="outlined" />
                                </Box>

                                <Box height={310}>
                                    {loading ? (
                                        <Skeleton variant="rounded" height={310} />
                                    ) : (
                                        <Bar data={barData} options={chartOptions} />
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Card sx={{ borderRadius: 3, height: 420 }}>
                            <CardContent sx={{ height: '100%' }}>
                                <Typography variant="h6" fontWeight={700}>
                                    Composição
                                </Typography>

                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Participação por indicador
                                </Typography>

                                <Box height={300}>
                                    {loading ? (
                                        <Skeleton variant="rounded" height={300} />
                                    ) : values.some(value => value > 0) ? (
                                        <Doughnut data={doughnutData} options={doughnutOptions} />
                                    ) : (
                                        <Box
                                            height="100%"
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                        >
                                            <Typography color="text.secondary">
                                                Nenhum dado disponível.
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12}>
                        <Card sx={{ borderRadius: 3 }}>
                            <CardContent>
                                <Typography variant="h6" fontWeight={700}>
                                    Análise rápida
                                </Typography>

                                <Grid container spacing={2} sx={{ mt: 1 }}>
                                    <Grid item xs={12} sm={4}>
                                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                Usuários por estabelecimento
                                            </Typography>

                                            <Typography variant="h5" fontWeight={800}>
                                                {loading ? (
                                                    <Skeleton width={80} />
                                                ) : (
                                                    usersPerEstablishment.toFixed(1)
                                                )}
                                            </Typography>

                                            <Typography variant="caption" color="text.secondary">
                                                Média simples considerando todos os estabelecimentos
                                            </Typography>
                                        </Paper>
                                    </Grid>

                                    <Grid item xs={12} sm={4}>
                                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                Usuários por empresa parceira
                                            </Typography>

                                            <Typography variant="h5" fontWeight={800}>
                                                {loading ? (
                                                    <Skeleton width={80} />
                                                ) : (
                                                    usersPerClientCompany.toFixed(1)
                                                )}
                                            </Typography>

                                            <Typography variant="caption" color="text.secondary">
                                                Média simples considerando empresas cliente
                                            </Typography>
                                        </Paper>
                                    </Grid>

                                    <Grid item xs={12} sm={4}>
                                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                Empresas por estabelecimento
                                            </Typography>

                                            <Typography variant="h5" fontWeight={800}>
                                                {loading ? (
                                                    <Skeleton width={80} />
                                                ) : (
                                                    clientCompaniesPerEstablishment.toFixed(1)
                                                )}
                                            </Typography>

                                            <Typography variant="caption" color="text.secondary">
                                                Relação operacional entre empresas e estabelecimentos
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>
        </LayoutBaseDePagina>
    );
};